import { compareDates } from "../../../../utils/date";
import DutyStatusLog, { type IAnnotation } from "../../../../model/DutyStatusLog";
import idSerializer from "geotab-uuid";
import { SITUATIONAL_DRIVING_CLEAR, type THosStatus } from "hos-tools";
import { DutyStatusLogService } from "../../../../model/DutyStatusLogs";

export interface IReviewChangesParams {
    exemptionAction?: EditPcYmAction;
    logId: string;
    newSdcDateTime?: string;
    isRequestedEdit?: boolean;
}

export enum EditPcYmAction {
    Remove = "Remove",
    Shorten = "Shorten"
}

export enum ShortenYmPcError {
    PreceedsStartTime = "PreceedsStartTime",
    ExceedsEndTime = "ExceedsEndTime",
    ExemptionTooShortToShorten = "ExemptionTooShortToShorten"
}

type ExemptionType = "PC" | "YM";

const BOUNDARY_LOG_OFFSET_MS = 10;

const LOG_STATUSES_TO_REMOVE = ["PC", "YM"];
const LOG_STATUSES_TO_REMOVE_WITH_INT_PC = [...LOG_STATUSES_TO_REMOVE, "INT_PC"] as const;

export const getMinMaxAllowedShorteningTimes = (currentLog: DutyStatusLog, nextLog: DutyStatusLog) => {
    const minAllowedTime = new Date(currentLog.dateTime);
    minAllowedTime.setUTCSeconds(0, 0);
    minAllowedTime.setUTCMinutes(minAllowedTime.getUTCMinutes() + 1);

    const maxAllowedTime = new Date(nextLog.dateTime);
    maxAllowedTime.setUTCSeconds(0, 0);
    maxAllowedTime.setUTCMinutes(maxAllowedTime.getUTCMinutes() - 1);
    return { minAllowedTime, maxAllowedTime };
};

// Validates the selected time for YM/PC exemption operations (shorten/remove). Uses UTC time calculations.
export const validateExemptionTimeModification = (
    dateTime: Date,
    currentLog: DutyStatusLog,
    nextLog: DutyStatusLog
): ShortenYmPcError | null => {
    // Calculate rounded time boundaries
    const { minAllowedTime, maxAllowedTime } = getMinMaxAllowedShorteningTimes(currentLog, nextLog);

    // Check if exemption period has enough rounded minutes to allow shortening
    if (minAllowedTime > maxAllowedTime) {
        return ShortenYmPcError.ExemptionTooShortToShorten;
    }

    // Check if selected time is within valid range
    if (dateTime < minAllowedTime) {
        return ShortenYmPcError.PreceedsStartTime;
    }

    if (dateTime > maxAllowedTime) {
        return ShortenYmPcError.ExceedsEndTime;
    }

    return null;
};

// Extracts all logs within a single YM/PC exemption period.
export function getExemptionPeriodLogs(logBeingEdited: DutyStatusLog, allLogs: DutyStatusLog[], dutyStatusLogService: DutyStatusLogService): DutyStatusLog[] {
    const startIndex = allLogs.findIndex(log => log.id === logBeingEdited.id);

    // Find the next SDC log after startIndex
    let endIndex = -1;
    for (let i = startIndex; i < allLogs.length; i++) {
        if (allLogs[i].isSituationalDrivingClear()) {
            endIndex = i;
            break;
        }

        if (dutyStatusLogService.isLogThatEndsExemptionPeriod(allLogs[i], logBeingEdited)) {
            break;
        }
    }
    return allLogs.slice(startIndex, endIndex + 1);
}

const convertExemptionLogToStandardStatus = (log: DutyStatusLog, exemptionType: ExemptionType): THosStatus | null => {
    switch (log.status) {
        case "DrivingWhileInExemption":
            return "D";
        case "DrivingStoppedWhileInExemption":
        case "PC":
        case "YM":
            return exemptionType === "YM" ? "ON" : "OFF";
        case "SituationalDrivingClear":
        case "INT_PC":
            return null;
        default:
            return log.status;
    }
};

// Gets the log at or before a given date, excluding INT_PC
const getLowerBoundaryLog = (logs: DutyStatusLog[], toDate: string) => {
    for (let i = logs.length - 1; i >= 0; i--) {
        if (compareDates(logs[i].dateTime, toDate) >= 0) {
            return logs[i];
        }
    }
    return null;
};

const filterLogsAfterDate = (logs: DutyStatusLog[], fromDate: string) => logs.filter(log => compareDates(fromDate, log.dateTime) > 0);

// Validates exemption period and gets exemption type
export const getExemptionTypeIfValid = (logs: DutyStatusLog[]): ExemptionType | null => {
    if (!logs?.length) {
        return null;
    }

    const firstStatus = logs[0].status;
    return (firstStatus === "PC" || firstStatus === "YM") && logs[logs.length - 1].isSituationalDrivingClear() ? firstStatus : null;
};

const getExemptionEditAnnotationComment = (exemptionType: ExemptionType): string =>
    exemptionType === "YM" ? "Replaces edited YM time" : "Replaces edited PC time";

const createAnnotation = (driver: DutyStatusLog["driver"], exemptionType: ExemptionType, dateTime: string): IAnnotation => ({
    id: idSerializer.get(),
    dateTime,
    driver,
    comment: getExemptionEditAnnotationComment(exemptionType)
});

const createLog = (previousLog: DutyStatusLog, logDateTime: string, logStatus: THosStatus, exemptionType: ExemptionType) => {
    const log = new DutyStatusLog({
        id: idSerializer.get(),
        dateTime: logDateTime,
        driver: previousLog.driver,
        device: previousLog.device,
        status: logStatus,
        origin: "Manual",
        coDrivers: previousLog.coDrivers || []
    });
    log.addAnnotation(createAnnotation(previousLog.driver, exemptionType, logDateTime));
    return log;
};

const createGapLogsFromExemptionLogs = (originalLogs: DutyStatusLog[], exemptionType: ExemptionType, exemptionLog?: DutyStatusLog): DutyStatusLog[] => {
    const result: DutyStatusLog[] = [];
    let previousStatus: THosStatus | null = null;

    const logCollection = exemptionLog ? [exemptionLog, ...originalLogs] : originalLogs;
    for (const log of logCollection) {
        const convertedStatus = convertExemptionLogToStandardStatus(log, exemptionType);
        if (convertedStatus && convertedStatus !== previousStatus) {
            result.push(createLog(log, log.dateTime, convertedStatus, exemptionType));
            previousStatus = convertedStatus;
        }
    }
    return result;
};

// Main function: calculates logs needed when exemption is shortened or removed
export function fillExemptionGap(
    exemptionPeriodLogs: DutyStatusLog[],
    exemptionLog: DutyStatusLog,
    selectedDateTime: Date | null
): DutyStatusLog[] {
    const exemptionType = exemptionLog.status as ExemptionType;
    const drivingInExemptionRelatedLogs = exemptionPeriodLogs.filter(log => log.isDrivingWhileInExemptionRelatedLog());

    if (!selectedDateTime) {
        // Exemption removal: convert all logs to standard format
        return createGapLogsFromExemptionLogs(drivingInExemptionRelatedLogs, exemptionType, exemptionLog);
    }

    // Determine boundary log status based on preceding activity
    const endDate = selectedDateTime.toISOString();
    const logsAfterEndDate = filterLogsAfterDate(drivingInExemptionRelatedLogs, endDate);
    const previousLog = getLowerBoundaryLog(drivingInExemptionRelatedLogs, endDate) || exemptionLog;
    const boundaryStatus = convertExemptionLogToStandardStatus(previousLog, exemptionType) as THosStatus;

    // Create boundary log 10ms after endDate for shortening actions
    const boundaryLog = createLog(previousLog, new Date(selectedDateTime.getTime() + BOUNDARY_LOG_OFFSET_MS).toISOString(), boundaryStatus, exemptionType);

    // Convert logs within the gap
    const finalGapLogs: DutyStatusLog[] = createGapLogsFromExemptionLogs(logsAfterEndDate, exemptionType);
    return [boundaryLog, ...finalGapLogs];
}

export function getLogsToRemove(
    exemptionPeriodLogs: DutyStatusLog[],
    selectedDateTime: Date | null,
    isIntPcRetentionEnabled = false
): DutyStatusLog[] {
    const baseStatuses = isIntPcRetentionEnabled ? LOG_STATUSES_TO_REMOVE : LOG_STATUSES_TO_REMOVE_WITH_INT_PC;
    let logStatusesToRemove = selectedDateTime ? baseStatuses : [...baseStatuses, SITUATIONAL_DRIVING_CLEAR];
    if (!selectedDateTime) {
        // For a removal scenario, pick all logs in the period including SDC, except DWIE and SWIE logs.
        return exemptionPeriodLogs.filter(log => logStatusesToRemove.indexOf(log.status) > -1);
    }

    // For a shortening scenario, pick all logs after selected time for removal
    return filterLogsAfterDate(exemptionPeriodLogs.filter(log => logStatusesToRemove.indexOf(log.status) > -1), selectedDateTime.toISOString());
}

// Returns SDC log with updated time when shortening a PC/YM exemption
export function getSdcLogWithUpdatedTime(sdcLog: DutyStatusLog, selectedDateTime: Date | null): DutyStatusLog | null {
    return selectedDateTime ? new DutyStatusLog({ ...sdcLog, dateTime: selectedDateTime.toISOString() }) : null;
}

// Check if a datetime falls within a previous DWIE (Driving While In Exemption) period that has been converted to a manual D log
export function isDateTimeInPreviousDwieRange(targetDateTime: string, dutyStatusLogService: DutyStatusLogService): boolean {
    const systemLogs = dutyStatusLogService.getSystemHOSLogs();
    if (!systemLogs?.length) {
        return false;
    }

    const drivingAndStoppedWhileInExemptionLogs = systemLogs.filter(log => log.isDrivingWhileInExemptionRelatedLog());
    if (!drivingAndStoppedWhileInExemptionLogs?.length) {
        return false;
    }

    // Find the most recent DWIE log at or before the target time
    const mostRecentLog = drivingAndStoppedWhileInExemptionLogs
        .filter(log => compareDates(log.dateTime, targetDateTime) >= 0)
        .sort((a, b) => compareDates(a.dateTime, b.dateTime))[0];

    if (mostRecentLog?.status === "DrivingWhileInExemption") {
        // Check if there's a SWIE that closes this DWIE period, otherwise this would be an ongoing exemption.
        // Target is in closed period if it falls between DWIE and SWIE
        return drivingAndStoppedWhileInExemptionLogs.some(
            log => log.status === "DrivingStoppedWhileInExemption" && compareDates(log.dateTime, targetDateTime) <= 0
        );
    }
    return false;
}
